import { useEffect, useMemo, useRef } from 'react';
import type { DependencyList } from 'react';

import { useMergedEntities } from './useMergedEntities';
/**
 *
 * @description hook that handles the logic behind the virtual list for infinite scrolling
 *
 * @param initialRefValue: initial value of the ref, should be whatever we get from cursor_next in the meta
 * @param updateCursorCallback this usually comes from localState (or redux) for example, this is the function that updates the filters
 * @param cleanInternalData: you should get this from the mergeEntities hook and pass it down here
 * @param resetConditions: these are the deps that will trigger a cleanInternalData
 * @param cursorNext: cursor next from the meta network, should be undefined if you do not want it to update
 * @param data: an array with your data
 * @param isFetching: boolean
 */
export function useVirtualList<T>({
  initialRefValue,
  updateCursorCallback,
  resetConditions,
  cursorNext,
  data,
  isFetching,
  uniqueKey
}: {
  initialRefValue?: string | null | number;
  updateCursorCallback: (...args: any) => unknown;
  resetConditions: DependencyList;
  cursorNext?: string | null | number;
  tableHeight?: string;
  data: T[] | undefined;
  isFetching: boolean;
  uniqueKey: string;
}) {
  // Maintain a ref for the current cursor value
  const cursorNextRef = useRef<string | number | undefined | null>(
    initialRefValue
  );

  const { dataInternal, cleanInternalData, updateElement, removeElement } =
    useMergedEntities<T>({
      data: data ?? [],
      shouldRefresh: !isFetching,
      uniqueKey
    });

  useEffect(() => {
    updateCursorCallback(undefined);
    cleanInternalData();
  }, [cleanInternalData, ...resetConditions]);

  // Update the ref whenever cursor_next changes
  useEffect(() => {
    cursorNextRef.current = cursorNext || undefined;
  }, [cursorNext]);

  const onScroll = useMemo(() => {
    let ticking = false;

    return (e: React.UIEvent<HTMLElement>) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;

        const el = e.target as HTMLDivElement;
        if (!el) {
          return;
        }

        const scrollArea = el.scrollHeight - el.clientHeight;

        const remaining = scrollArea - el.scrollTop;

        const remainingPercentage = remaining / Math.max(scrollArea, 1);
        if (remainingPercentage < 0.25) {
          if (cursorNextRef.current) {
            updateCursorCallback(cursorNextRef.current);
          }
        }
      });
    };
  }, [updateCursorCallback]);

  return {
    onScroll,
    data: dataInternal,
    optimisticallyUpdateElement: updateElement,
    removeElement
  };
}

export const TABLE_HEIGHT = '60vh';
export const TABLE_MIN_WIDTH = 800;
